import api from '../utils/api.js';
import { userFacingDetail } from '../utils/errorHandler.js';

/**
 * AI マッチング (SS-02) API 呼び出し集約層
 *
 * エンドポイント一覧はプロジェクト設定.md §6「拡張機能 API エンドポイント一覧」準拠。
 * PUT /api/matching/reorder・GET /api/matching/quota・
 * GET /api/matching/job-pool/{id}/attachment/download_url は Issue #443 (Issue #413 carry-over)
 * でBE実装済み(PR #113)。GET /api/matching/{opportunity_id} (直近の結果の取得、FR-MATCH-005)
 * は Issue #540 でBE実装済み。
 */

/**
 * 空文字列 ('') を null に正規化する。
 * MatchingFilterForm の年収 select は「指定なし」選択時に空文字列を返すが、
 * BE の MatchingFilters (Pydantic) は Optional[int] = None であり空文字列は整数として
 * パースできずバリデーションエラーになる（Issue #535）。null/undefined はそのまま null 化する。
 *
 * @param {string|number|null|undefined} v
 * @returns {number|null}
 */
const toNullableInt = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// AI マッチングの実行の失敗の文言（画面レイアウト_SCR-M-001.md §6.3
// 「AI マッチングの実行のエラー」の表の No. と対応。Issue #868）
export const EXECUTE_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    // No.2。要求が届いて Phase 2（AI 呼出・ポイント消費）まで進んでいる場合がある
    // （取込の表 No.8 と同じ状況）。「届いていない」とだけ書かない
    network:
        '通信が途中で終了しました。実行が済んでいる場合があります。'
        + 'ページを再読み込みして、推薦結果が表示されるかを確認してから、必要に応じて再試行してください。',
    failed: 'AIマッチングの実行に失敗しました。', // No.4・No.5（サーバの detail が無い場合）
};

/**
 * AI マッチングを実行し、推薦候補一覧を取得する (FR-MATCH-001)。
 * 選択した求人と条件が同じで 24 時間以内に実行済みの場合はキャッシュが返る(AIポイント未消費)。
 *
 * 対象にする求人は filters.job_pool_item_ids で送る。全件を選んだときも ID を全部送り、
 * 業界 (industry) は送らない (画面レイアウト_SCR-M-001.md §6.4.1、Issue #779)。
 * BE (#956) が先にデプロイされている前提: 古い BE はこの項目を黙って無視し全件で実行する。
 *
 * filters.top_n には選択した求人の件数を送り、条件に合う求人を件数で切らない
 * (SCR-M-001 §4.6 論点 8、Issue #1774)。1 件・2〜4 件は BE の下限 1 が先にデプロイされている前提
 * (下限が 5 のままの BE は 422 を返す)。
 *
 * @param {string} opportunityId
 * @param {{salaryMin?: number, salaryMax?: number}} filters
 * @param {string[]} jobPoolItemIds - マッチングの対象にする求人 (IN-M-007)
 * @returns {Promise<{ok: boolean, status: number, retryable?: boolean, data?: object, errorMessage?: string}>}
 *   retryable は**手動の**再試行を出してよいか (SCR-M-001 §6.3「AI マッチングの実行のエラー」)。
 *   自動リトライはしない (同表の注記。Issue #868)
 */
export async function executeMatching(opportunityId, filters = {}, jobPoolItemIds = []) {
    const body = {
        opportunity_id: opportunityId,
        filters: {
            job_pool_item_ids: jobPoolItemIds,
            salary_min: toNullableInt(filters.salaryMin),
            salary_max: toNullableInt(filters.salaryMax),
            top_n: jobPoolItemIds.length > 0 ? jobPoolItemIds.length : null,
        },
    };

    let response;
    try {
        response = await api.fetch('/api/matching/execute', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch {
        // 通信の失敗（応答を受け取れない）。api.js は fetch の例外を再送出するため、
        // ここで受けないと呼出側に伝わらず、画面が「実行中」のまま止まる
        // （画面レイアウト_SCR-M-001.md §6.3「AI マッチングの実行のエラー」No.2）
        return { ok: false, status: 0, retryable: true, errorMessage: EXECUTE_ERROR_MESSAGES.network };
    }

    // 401 のトークン更新に失敗した場合。api.js が /login へ移すため再試行しても意味がない（No.1）
    if (!response) {
        return { ok: false, status: 0, retryable: false, errorMessage: EXECUTE_ERROR_MESSAGES.loginRequired };
    }

    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return {
            ok: false,
            status: response.status,
            // 4xx はサーバが要求を受け付けないと判断しており、やり直しても同じ結果になる（No.4）。
            // 5xx は一時的な失敗のことがあるため手動の再試行を許す（No.5）。自動リトライはしない
            retryable: response.status >= 500,
            errorMessage: userFacingDetail(response.status, errData) || EXECUTE_ERROR_MESSAGES.failed,
        };
    }

    const data = await response.json();
    return { ok: true, status: response.status, data };
}

/**
 * 直近のマッチング結果を取得する (FR-MATCH-005、Issue #540/#541)。実行からの経過時間は問わない
 * (Issue #710・#709。キャッシュの有効期限は再実行で前回の結果を再利用するかの判定だけに使う。SCR-M-001 §6.4.1)。
 * AIマッチング画面から別画面へ遷移し再度戻ってきた際、直前の推薦結果を自動復元するために使用する
 * (Reactローカル状態はアンマウントで失われるため)。抽出条件(filters)は問わず最新の1件を取得する。
 * 該当結果が無い場合(未実行)は 200 と本文 null が返る (Issue #947。SCR-M-001 §6.4.1。BE の #709 の切替の前は、
 * 24 時間を過ぎた結果もこれになる)。画面は応答の created_at・expires_at で表示するかを決めない。
 * BE の切替が済むまでは 404 が返るため、200 の null と 404 のどちらも「結果なし」として扱う
 * (エラーではないため errorMessage は設定せず、ok: false のみで通知する)。
 * 404以外の失敗(401/500等)はバックグラウンド復元処理のためユーザーへは表示しないが、
 * 開発者が気づけるよう console.error に記録する(Step2レビュー指摘、Issue #541)。
 * 通信の失敗も「結果なし」として返し、例外を投げない (Issue #1388)。
 *
 * @param {string} opportunityId
 * @returns {Promise<{ok: boolean, data?: object}>}
 */
export async function fetchLatestMatchingResult(opportunityId) {
    let response;
    try {
        response = await api.fetch(`/api/matching/${encodeURIComponent(opportunityId)}`);
    } catch {
        // 通信の失敗は「結果なし」(Issue #1388。SCR-M-001 §6.3「画面を表示したときの取得の失敗」)。
        // api.fetch が console に 1 行出しているため、ここでは出さない。
        // 下の本文の解析の try (Issue #947) とは別の失敗であり、外側を二重に囲まない
        return { ok: false };
    }

    if (!response) {
        return { ok: false };
    }

    if (!response.ok) {
        if (response.status !== 404) {
            console.error(
                `fetchLatestMatchingResult: unexpected status ${response.status} for opportunityId=${opportunityId}`
            );
        }
        return { ok: false };
    }

    let data;
    try {
        data = await response.json();
    } catch (err) {
        // 空ボディ等で解析できない応答は正本 (PBI-002 §11.2) の違反。例外を呼び出し元へ投げると
        // 画面の初期化が止まり「取得中」のままになるため「結果なし」に倒し、異常は console に残す (Issue #947)
        console.error(
            `fetchLatestMatchingResult: invalid JSON body for opportunityId=${opportunityId}`,
            err
        );
        return { ok: false };
    }
    // 200 の null は「結果なし」(Issue #947)。呼び出し元は data の中身を読むため、ok: true で返さない
    if (data === null) {
        return { ok: false };
    }
    return { ok: true, data };
}

/**
 * AIポイント残量を取得する (DP-M-002、Issue #443)。
 *
 * @returns {Promise<{ok: boolean, data?: {used_points: number, max_points: number, remaining_points: number}, errorMessage?: string}>}
 */
export async function fetchQuota() {
    let response;
    try {
        response = await api.fetch('/api/matching/quota');
    } catch {
        // 通信の失敗 (Issue #1388)。api.fetch が console に 1 行出しているため、ここでは出さない。
        // 成功の応答で本文を解析できないときは例外のまま投げ、呼び出し側 (useAiMatching の loadQuota) が
        // 捕まえる (捕まえる場所を 1 つにするため。SCR-M-001 §6.3「画面を表示したときの取得の失敗」)
        return { ok: false, errorMessage: 'AIポイント残量の取得に失敗しました。' };
    }

    if (!response) {
        return { ok: false, errorMessage: 'ログインが必要です。' };
    }

    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return { ok: false, errorMessage: userFacingDetail(response.status, errData) || 'AIポイント残量の取得に失敗しました。' };
    }

    const data = await response.json();
    return { ok: true, data };
}

// 並べ替えの保存の失敗の文言（画面レイアウト_SCR-M-001.md §6.3
// 「並べ替えの保存の失敗」の表の No. と対応。Issue #2173）
export const REORDER_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    // No.2。要求が届いて並びとマッチ度の保存まで済んでいる場合がある。「保存されていない」とは書かない。
    // 成功の応答で推薦結果の要素を読めない場合 (付け直しの前の BE を含む) も同じ文言にする
    network:
        '通信が途中で終了しました。並び順とマッチ度が保存されている場合があります。'
        + 'ページを再読み込みして確認してください。',
    // No.3（サーバの detail が文字列でない場合と、入力の検査の 422）。失敗すると数字が並びと逆に
    // 見えるため、数字も直っていないことを 2 文目で書く
    failed: '並べ替えを保存できませんでした。マッチ度も付け直していません。',
};

// 応答の推薦結果の要素を、画面が読める形か。どの要素も求人 ID を持つオブジェクトであること
// (1 つでも読めなければ、保存後の値を応答から読めないとみなす。§6.3「並べ替えの保存の失敗」No.2)
function isReadableRecommendations(recommendations) {
    return Array.isArray(recommendations)
        && recommendations.every(
            (rec) => rec !== null && typeof rec === 'object' && typeof rec.job_pool_item_id === 'string'
        );
}

/**
 * AIマッチング結果の手動並べ替えを保存し、同じ 1 回の保存でマッチ度を上から高い順に付け直す
 * (BTN-M-004、Issue #443・#2173。SCR-M-001 §6.2「並べ替えの保存でマッチ度を付け直す」・§6.4.2)。
 * AI推薦順(matching_results.recommendations の rank)自体は変更されず、
 * 担当者確定順が matching_reorder_history に実施証跡として記録される。
 *
 * 付け直すことの指定 (renumber_scores: true) を必ず送る。付け直す値はサーバが保存済みの値から決める
 * (画面では計算しない)。応答の recommendations (保存後の推薦候補) が無い・読めないときは、付け直しの前の
 * BE に当たったか、保存後の値を読めないため、通信の失敗と同じ文言にする (§6.4.2「応答」)。
 *
 * @param {string} matchingResultId
 * @param {string[]} orderedIds
 * @returns {Promise<{ok: boolean, recommendations?: object[], errorMessage?: string}>}
 */
export async function saveReorder(matchingResultId, orderedIds) {
    const result = await putMatchingSetting(
        '/api/matching/reorder',
        { matching_result_id: matchingResultId, ordered_ids: orderedIds, renumber_scores: true },
        REORDER_ERROR_MESSAGES
    );
    if (!result.ok) return result;
    const recommendations = result.data?.recommendations;
    if (!isReadableRecommendations(recommendations)) {
        return { ok: false, errorMessage: REORDER_ERROR_MESSAGES.network };
    }
    return { ok: true, recommendations };
}

/**
 * 求人プールアイテムに紐づく求人票ファイルの署名付きダウンロードURLを取得する (BTN-M-006、Issue #443)。
 * 応答の file_type (添付の MIME type) も返す (Issue #1405。PDF かどうかの判断に使う)。
 * 応答に file_type の項目が無い (BE の反映前) ときは undefined、null (BE が種類を知らない) は null のまま返す。
 * 呼び出し元は、項目が無いときだけファイル名で判断し、null は PDF でない側に倒す (BE の attachment と揃える)。
 *
 * @param {string} jobPoolItemId
 */
export async function fetchJobPoolItemAttachmentUrl(jobPoolItemId) {
    const response = await api.fetch(
        `/api/matching/job-pool/${encodeURIComponent(jobPoolItemId)}/attachment/download_url`
    );

    if (!response) {
        return { ok: false, errorMessage: 'ログインが必要です。' };
    }

    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return {
            ok: false,
            errorMessage: response.status === 404
                ? '求人票ファイルが見つかりません。'
                : (userFacingDetail(response.status, errData) || '求人票ファイルの取得に失敗しました。'),
        };
    }

    const data = await response.json();
    return {
        ok: true,
        downloadUrl: data.download_url,
        fileName: data.file_name,
        fileType: 'file_type' in data ? data.file_type : undefined,
    };
}

/**
 * AIマッチング推薦候補一覧をPDF帳票(RPT-M-001)として取得する (BTN-M-003、T-003-U1、Issue #470)。
 * 並べ替え順は BE 側が matching_reorder_history から自動解決するため、リクエストボディは不要
 * (GET単一エンドポイント、backend/app/routers/matching.py::export_matching_pdf_endpoint)。
 *
 * どの結果から作るかは、画面に表示中の結果の ID を matching_result_id で必ず送って指定する
 * (SCR-M-001 §4.3 BTN-M-003、Issue #878)。サーバは指定が無ければ最新の結果を使うが、
 * それでは前回の結果を再利用して古い結果を表示しているときに画面と違う PDF ができるため、
 * 画面からは指定の無い要求を出さない。
 *
 * @param {string} opportunityId
 * @param {string} matchingResultId 画面に表示中のマッチング結果の ID (並べ替えの保存と同じ値)
 * @returns {Promise<{ok: boolean, blob?: Blob, fileName?: string, errorMessage?: string}>}
 */
export async function exportMatchingPdf(opportunityId, matchingResultId) {
    let response;
    try {
        response = await api.fetch(
            `/api/matching/${encodeURIComponent(opportunityId)}/pdf`
            + `?matching_result_id=${encodeURIComponent(matchingResultId)}`
        );
    } catch {
        return { ok: false, errorMessage: 'PDFの出力に失敗しました。' };
    }

    if (!response) {
        return { ok: false, errorMessage: 'ログインが必要です。' };
    }

    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return {
            ok: false,
            errorMessage: userFacingDetail(response.status, errData) || 'PDFの出力に失敗しました。',
        };
    }

    const blob = await response.blob();

    // Content-Disposition: attachment; filename*=UTF-8''<encoded> (RFC 5987) からファイル名を抽出
    const disposition = response.headers.get('content-disposition') || '';
    const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    const fileName = match ? decodeURIComponent(match[1]) : 'matching-recommendation.pdf';

    return { ok: true, blob, fileName };
}

// マッチ度の表示の設定の保存の失敗の文言（画面レイアウト_SCR-M-001.md §6.3
// 「マッチ度の表示の設定の保存の失敗」の表の No. と対応。Issue #1435）
export const SCORE_VISIBILITY_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    // No.2。要求が届いて保存まで済んでいる場合がある。「保存されていない」とは書かない
    network:
        '通信が途中で終了しました。設定が保存されている場合があります。'
        + 'ページを再読み込みして確認してください。',
    failed: 'マッチ度の表示の設定を保存できませんでした。', // No.3（サーバの detail が文字列でない場合と、入力の検査の 422）
};

/**
 * マッチ度の表示・非表示の設定を保存する (IN-M-009、SCR-M-001 §6.4.5、Issue #1435)。
 * 設定は推薦結果ごとで、画面と、その結果から作る推薦 PDF の両方に効く。AI ポイントは消費しない。
 *
 * 応答は推薦結果の全体 (実行・画面復帰と同じ形。PBI-002 §11.8)。呼び出し元は応答の score_hidden で
 * 設定を読み直す (画面が送った値をそのまま信じない)。
 *
 * @param {string} matchingResultId 画面に表示中のマッチング結果の ID (並べ替えの保存・PDF 出力と同じ値)
 * @param {boolean} scoreHidden 非表示にするなら true
 * @returns {Promise<{ok: boolean, data?: object, errorMessage?: string}>}
 */
export async function saveScoreHidden(matchingResultId, scoreHidden) {
    const result = await putScoreAdjustments(
        { matching_result_id: matchingResultId, score_hidden: scoreHidden },
        SCORE_VISIBILITY_ERROR_MESSAGES
    );
    if (!result.ok) return result;
    // 保存後の設定が応答に無いと、画面は保存できたかを知れない (通信の失敗と同じ文言にする)
    if (typeof result.data?.score_hidden !== 'boolean') {
        return { ok: false, errorMessage: SCORE_VISIBILITY_ERROR_MESSAGES.network };
    }
    return result;
}

// マッチ度の数字の保存の失敗の文言（画面レイアウト_SCR-M-001.md §6.3
// 「マッチ度の数字の保存の失敗」の表の No. と対応。Issue #1435 段階 ②）
export const SCORE_OVERRIDE_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    // No.2。要求が届いて保存まで済んでいる場合がある。「保存されていない」とは書かない
    network:
        '通信が途中で終了しました。マッチ度が保存されている場合があります。'
        + 'ページを再読み込みして確認してください。',
    failed: 'マッチ度を保存できませんでした。', // No.3（サーバの detail が文字列でない場合と、入力の検査の 422）
};

/**
 * 求人 1 件のマッチ度を、利用者が直した値で保存する (IN-M-010・BTN-M-011・BTN-M-013、
 * SCR-M-001 §6.4.5、Issue #1435 段階 ②)。AI が出した値 (score) は書き換わらず、
 * 手で直した値 (score_override) が別に保存される。AI ポイントは消費しない。
 *
 * 表示の設定 (score_hidden) は送らない (省略すると、サーバは表示の設定を変えない。PBI-002 §11.8)。
 *
 * @param {string} matchingResultId 画面に表示中のマッチング結果の ID
 * @param {string} jobPoolItemId 直す求人の ID
 * @param {number|null} score 0〜100 の整数。null は「手で直した値を消す」(AI の値に戻す)
 * @returns {Promise<{ok: boolean, recommendation?: object, errorMessage?: string}>}
 *   recommendation: 応答の recommendations のうち、保存した求人の要素 (保存後の値はここから読む)
 */
export async function saveScoreOverride(matchingResultId, jobPoolItemId, score) {
    const result = await putScoreAdjustments(
        {
            matching_result_id: matchingResultId,
            score_overrides: [{ job_pool_item_id: jobPoolItemId, score }],
        },
        SCORE_OVERRIDE_ERROR_MESSAGES
    );
    if (!result.ok) return result;
    const recommendations = result.data?.recommendations;
    const saved = Array.isArray(recommendations)
        ? recommendations.find((rec) => rec?.job_pool_item_id === jobPoolItemId)
        : undefined;
    // 保存した求人の要素が応答に無いと、画面は保存後の値を読めない (通信の失敗と同じ文言にする)
    if (!saved) {
        return { ok: false, errorMessage: SCORE_OVERRIDE_ERROR_MESSAGES.network };
    }
    return { ok: true, recommendation: saved };
}

// 求職者に提案するかの設定の保存の失敗の文言（画面レイアウト_SCR-M-001.md §6.3
// 「求職者に提案するかの設定の保存の失敗」の表の No. と対応。Issue #992）
export const CANDIDATE_VISIBILITY_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    // No.2。要求が届いて保存まで済んでいる場合がある。「保存されていない」とは書かない
    network:
        '通信が途中で終了しました。設定が保存されている場合があります。'
        + 'ページを再読み込みして確認してください。',
    failed: '求職者に提案するかの設定を保存できませんでした。', // No.3（サーバの detail が文字列でない場合と、入力の検査の 422）
};

/**
 * 求人 1 件を推薦 PDF に載せるか (求職者に提案するか) を保存する (IN-M-011、SCR-M-001 §6.4.6、
 * Issue #992)。マッチ度の保存とは別の API (PBI-002 §11.9)。AI ポイントは消費しない。
 *
 * @param {string} matchingResultId 画面に表示中のマッチング結果の ID (並べ替えの保存・PDF 出力と同じ値)
 * @param {string} jobPoolItemId 切り替えた求人の ID
 * @param {boolean} visible 載せるなら true
 * @returns {Promise<{ok: boolean, recommendation?: object, errorMessage?: string}>}
 *   recommendation: 応答の recommendations のうち、保存した求人の要素 (保存後の値はここから読む)
 */
export async function saveCandidateVisibility(matchingResultId, jobPoolItemId, visible) {
    const result = await putMatchingSetting(
        '/api/matching/candidate-visibility',
        {
            matching_result_id: matchingResultId,
            candidate_visibility: [{ job_pool_item_id: jobPoolItemId, visible }],
        },
        CANDIDATE_VISIBILITY_ERROR_MESSAGES
    );
    if (!result.ok) return result;
    const recommendations = result.data?.recommendations;
    const saved = Array.isArray(recommendations)
        ? recommendations.find((rec) => rec?.job_pool_item_id === jobPoolItemId)
        : undefined;
    // 保存した求人の要素と、その保存後の値 (サーバは必ず真偽値を入れる) が応答に無いと、
    // 画面は保存できたかを知れない (通信の失敗と同じ文言にする。§6.3 No.2)
    if (!saved || typeof saved.visible_to_candidate !== 'boolean') {
        return { ok: false, errorMessage: CANDIDATE_VISIBILITY_ERROR_MESSAGES.network };
    }
    return { ok: true, recommendation: saved };
}

// おすすめポイントの文の保存の失敗の文言（画面レイアウト_SCR-M-001.md §6.3
// 「おすすめポイントの文の保存の失敗」の表の No. と対応。Issue #777）
export const REASON_OVERRIDE_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。', // No.1
    // No.2。要求が届いて保存まで済んでいる場合がある。「保存されていない」とは書かない
    network:
        '通信が途中で終了しました。おすすめポイントが保存されている場合があります。'
        + 'ページを再読み込みして確認してください。',
    failed: 'おすすめポイントを保存できませんでした。', // No.3（サーバの detail が文字列でない場合と、入力の検査の 422）
};

/**
 * 求人 1 件のおすすめポイントの文を、利用者が編集した文で保存する (IN-M-012・BTN-M-016・BTN-M-018、
 * SCR-M-001 §6.4.7、Issue #777)。AI が生成した文 (reasons) は書き換わらず、編集した文
 * (reasons_override) が別に保存される。AI ポイントは消費しない。入出力は PBI-002 §11.11。
 *
 * @param {string} matchingResultId 画面に表示中のマッチング結果の ID
 * @param {string} jobPoolItemId 文を編集する求人の ID
 * @param {string|null} text 前後の空白を除いた文。null は「編集した文を消す」(AI の文に戻す)
 * @returns {Promise<{ok: boolean, recommendation?: object, errorMessage?: string}>}
 *   recommendation: 応答の recommendations のうち、保存した求人の要素 (保存後の文はここから読む)
 */
export async function saveReasonOverride(matchingResultId, jobPoolItemId, text) {
    const result = await putMatchingSetting(
        '/api/matching/reason-overrides',
        {
            matching_result_id: matchingResultId,
            reason_overrides: [{ job_pool_item_id: jobPoolItemId, text }],
        },
        REASON_OVERRIDE_ERROR_MESSAGES
    );
    if (!result.ok) return result;
    const recommendations = result.data?.recommendations;
    const saved = Array.isArray(recommendations)
        ? recommendations.find((rec) => rec?.job_pool_item_id === jobPoolItemId)
        : undefined;
    // 保存した求人の要素が応答に無いと、画面は保存後の文を読めない (通信の失敗と同じ文言にする。§6.3 No.2)
    if (!saved) {
        return { ok: false, errorMessage: REASON_OVERRIDE_ERROR_MESSAGES.network };
    }
    return { ok: true, recommendation: saved };
}

/**
 * PUT /api/matching/score-adjustments を呼び、失敗を文言つきの結果に変える
 * (表示の設定の保存と、数字の保存で共通。入出力は PBI-002 §11.8)。
 *
 * @param {object} body 送る本文
 * @param {{loginRequired: string, network: string, failed: string}} messages 失敗の文言
 * @returns {Promise<{ok: boolean, data?: object, errorMessage?: string}>}
 */
function putScoreAdjustments(body, messages) {
    return putMatchingSetting('/api/matching/score-adjustments', body, messages);
}

/**
 * 推薦結果の設定を保存する PUT を呼び、失敗を文言つきの結果に変える
 * (マッチ度の保存〈PBI-002 §11.8〉・求職者に提案するかの保存〈§11.9。Issue #992〉・
 * おすすめポイントの文の保存〈§11.11。Issue #777〉で共通)。
 *
 * @param {string} path API のパス
 * @param {object} body 送る本文
 * @param {{loginRequired: string, network: string, failed: string}} messages 失敗の文言
 * @returns {Promise<{ok: boolean, data?: object, errorMessage?: string}>}
 */
async function putMatchingSetting(path, body, messages) {
    let response;
    try {
        response = await api.fetch(path, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch {
        return { ok: false, errorMessage: messages.network };
    }

    if (!response) {
        return { ok: false, errorMessage: messages.loginRequired };
    }

    if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return { ok: false, errorMessage: userFacingDetail(response.status, errData) || messages.failed };
    }

    try {
        return { ok: true, data: await response.json() };
    } catch {
        // 成功の応答で本文を読めない。保存は済んでいる場合があるため、通信の失敗と同じ文言にする
        return { ok: false, errorMessage: messages.network };
    }
}
